import { CommonModule } from "@angular/common";
import { Component, inject, OnInit, OnDestroy } from "@angular/core";
import { Router, RouterOutlet } from "@angular/router";
import { AuthService } from "../../core/services/auth.service";
import { User } from "../../core/models/user.interface";
import { PlanComponent } from "../pages/plan/plan.component";
import { SidebarComponent } from "../../core/shared/components/sidebar/sidebar.component";
import { CompleteDataProfileComponent } from "../pages/complete-data-profile/complete-data-profile.component";
import { VideoModalComponent } from "../pages/video-modal/video-modal.component";
import { Subscription } from "rxjs";
import { USER_TYPES, SUBSCRIPTION_TYPES } from "../../core/shared/constants";
import { NotificationsSidebarComponent } from "../../core/shared/components/notifications-sidebar/notifications-sidebar.component";

@Component({
  selector: "app-dashboard",
  standalone: true,
  imports: [
    RouterOutlet,
    CommonModule,
    PlanComponent,
    SidebarComponent,
    CompleteDataProfileComponent,
    NotificationsSidebarComponent,
    VideoModalComponent
],
  templateUrl: "./dashboard.component.html",
  styleUrl: "./dashboard.component.scss",
})
export class DashboardComponent implements OnInit, OnDestroy {
  private authService: AuthService = inject(AuthService);
  isCollapsed = false;
  user: User | null = this.authService.getUser();
  debounceResize: any;
  isResponsiveMenu: boolean = false;
  showPlanOverlay = false; // Se mostrará solo a usuarios Freemium que sean Clientes
  userPlan: string = "Freemium"; // Valor por defecto
  showCompleteDataOverlay = false;
  hasShownModalThisSession = false; // Para controlar que solo aparezca al inicio de sesión
  showVideoModal = false; // Para controlar la visibilidad del modal de video
  private sessionStartTime: number = Date.now();

  // Suscripción para actualizar el usuario cuando cambie
  private userSubscription: Subscription | null = null;

  constructor(private router: Router) { }

  /**
   * Valida si hay una sesión activa, si no redirige al login
   * @returns true si hay sesión válida, false si no hay sesión
   */
  private validateSession(): boolean {
    // Verificar si hay token y usuario
    const token = this.authService.getToken();
    const user = this.authService.getUser();
    
    // Si no hay token o no hay usuario, redirigir al login
    if (!token || !user) {
      console.warn('No hay sesión activa, redirigiendo al login');
      this.authService.signOutApply();
      this.router.navigate(['/auth']);
      return false;
    }
    
    // Actualizar el usuario en el componente
    this.user = user;
    return true;
  }

  ngOnInit() {
    // Validar sesión activa antes de continuar
    // Si no hay sesión, el método redirige y retorna false
    if (!this.validateSession()) {
      return; // No continuar si no hay sesión válida
    }
    
    this.checkIfResponsive();
    
    // Si es vista móvil, colapsar el sidebar por defecto
    if (this.isResponsiveMenu) {
      this.isCollapsed = true;
    }
    
    window.addEventListener("resize", () => {
      clearTimeout(this.debounceResize);
      this.debounceResize = setTimeout(() => this.checkIfResponsive(), 200);
    });

    // Verificar si es una nueva sesión
    this.checkNewSession();

    // Agregar listener para detectar cuando el usuario regresa a la aplicación
    document.addEventListener('visibilitychange', this.onVisibilityChange.bind(this));

    // Agregar listener para detectar cuando el usuario regresa a la página
    window.addEventListener('focus', this.onWindowFocus.bind(this));

    // Suscribirse a cambios en el usuario
    this.userSubscription = this.authService.userUpdatedObservable.subscribe(user => {
      if (user) {
        this.user = user;

        // Actualizar el tipo de suscripción si es necesario
        if (this.user.type && this.user.type.suscriptionType) {
          this.userPlan = this.user.type.suscriptionType.name;
        }

        // Mostrar modal de completar perfil solo al inicio de sesión si la información no está completa
        // Y solo para usuarios tipo customer/cliente
        if (!this.hasShownModalThisSession && !this.user.isCompleteInfo && this.isCustomer()) {
            this.showCompleteDataOverlay = true;
            this.hasShownModalThisSession = true;
            // Registrar que se mostró el modal en esta sesión
            sessionStorage.setItem('completeProfileModalLastShown', Date.now().toString());
        }

        // Lógica para mostrar el pop-up de planes - COMENTADO PARA NO MOSTRAR AUTOMÁTICAMENTE
        // if (this.isFreemiumUser() && (this.user.personalSection &&
        //   this.user.personalSection.completedProfile &&
        //   this.user.personalSection.completedProfile === true)) {
        //   this.showPlanOverlay = true;
        // }

      }
    });

    // Verificar si es un usuario Coach para redirigirlo a su dashboard específico
    if (this.isCoachUser() &&
      (this.router.url === '/dashboard' ||
        this.router.url === '/dashboard/edit-profile' ||
        this.router.url === '/dashboard/')) {
      this.router.navigate(["/dashboard/dashboard-coach"]);
    }

    if (this.isExpertUser() &&
      (this.router.url === '/dashboard' ||
        this.router.url === '/dashboard/edit-profile' ||
        this.router.url === '/dashboard/')) {
      this.router.navigate(["/dashboard/dashboard-experto"]);
    }

    // Verificar si es un usuario Executive para redirigirlo a su dashboard específico
    if (this.isExecutiveUser() &&
      (this.router.url === '/dashboard' ||
        this.router.url === '/dashboard/edit-profile' ||
        this.router.url === '/dashboard/')) {
      this.router.navigate(["/dashboard/dashboard-executive"]);
    }

    // Obtener el tipo de suscripción del usuario
    if (this.user && this.user.type && this.user.type.suscriptionType) {
      this.userPlan = this.user.type.suscriptionType.name;
    }

    // Lógica para mostrar el pop-up de planes al iniciar - COMENTADO PARA NO MOSTRAR AUTOMÁTICAMENTE
    // if (this.isFreemiumUser() && (this.user.personalSection &&
    //     this.user.personalSection.completedProfile &&
    //     this.user.personalSection.completedProfile === true)) {
    //     this.showPlanOverlay = true;
    // }

    // Validación inicial al cargar el componente
    // Solo para usuarios tipo customer/cliente
    if (this.user && !this.hasShownModalThisSession && !this.user.isCompleteInfo && this.isCustomer()) {
        this.showCompleteDataOverlay = true;
        this.hasShownModalThisSession = true;
    }

  }

  ngOnDestroy() {
    try {
      // Cancelar suscripción al destruir el componente
      if (this.userSubscription) {
        this.userSubscription.unsubscribe();
        this.userSubscription = null;
      }

      // Eliminar listener de resize
      if (this.debounceResize) {
        clearTimeout(this.debounceResize);
      }
      window.removeEventListener("resize", () => { });

      // Limpiar event listeners adicionales
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      window.removeEventListener('focus', this.onWindowFocus);

      // Limpiar event listener del submenú de perfil
      try {
        document.removeEventListener("click", this.closeProfileSubmenu);
      } catch (error) {
        console.error('Error al limpiar event listener del submenú:', error);
      }
    } catch (error) {
      console.error('Error en ngOnDestroy:', error);
    }
  }

  // Método para manejar cambios de visibilidad de la página
  private onVisibilityChange(): void {
    if (!document.hidden) {
      // El usuario regresó a la aplicación, verificar si es nueva sesión
      this.checkNewSession();
    }
  }

  // Método para manejar cuando la ventana recibe el foco
  private onWindowFocus(): void {
    this.checkNewSession();
  }

  // Método para verificar si es una nueva sesión
  private checkNewSession(): void {
    // Obtener la última vez que se mostró el modal desde sessionStorage
    const lastShownTime = sessionStorage.getItem('completeProfileModalLastShown');
    const currentTime = Date.now();
    
    // Si no hay registro o han pasado más de 30 minutos (1800000 ms), considerar nueva sesión
    if (!lastShownTime || (currentTime - parseInt(lastShownTime)) > 1800000) {
      // Es una nueva sesión, resetear el flag
      this.hasShownModalThisSession = false;
      console.log('Nueva sesión detectada, modal disponible');
    } else {
      // No es una nueva sesión, mantener el flag
      this.hasShownModalThisSession = true;
      console.log('Sesión existente, modal no disponible');
    }
  }

  // Método para verificar si el usuario es Freemium
  isFreemiumUser(): boolean {
    // Si el usuario es administrativo (id: 3) o coach (id: 2), no es Freemium
    if (this.user && this.user.type
      && (this.user.type.id === USER_TYPES.Administrator.id
        || this.user.type.id === USER_TYPES.Coach.id || this.user.type.id === USER_TYPES.Expert.id)) {
      return false;
    }

    // Para usuarios clientes (id: 1), verificar su plan
    if (!this.user || !this.user.type || !this.user.type.suscriptionType) {
      return true; // Por defecto consideramos Freemium si no hay datos
    }

    // Comparación exacta con 'Freemium'
    return this.user.type.suscriptionType.name === "Freemium";
  }

  checkIfResponsive() {
    try {
      const wasResponsive = this.isResponsiveMenu;
      this.isResponsiveMenu = window.innerWidth <= 768;
      
      // Si cambió a responsive, colapsar el sidebar por defecto
      if (!wasResponsive && this.isResponsiveMenu) {
        this.isCollapsed = true;
      }
      
      // Si cambió de responsive a no responsive y el submenú está abierto, cerrarlo
      if (wasResponsive && !this.isResponsiveMenu && this.showProfileSubmenu) {
        this.showProfileSubmenu = false;
        try {
          document.removeEventListener("click", this.closeProfileSubmenu);
        } catch (error) {
          console.error('Error al limpiar event listener en responsive change:', error);
        }
      }
    } catch (error) {
      console.error('Error en checkIfResponsive:', error);
    }
  }

  toggleMenu() {
    try {
      this.isCollapsed = !this.isCollapsed;
      
      // Cerrar el submenú de perfil si está abierto cuando se colapsa el sidebar
      if (this.isCollapsed && this.showProfileSubmenu) {
        this.showProfileSubmenu = false;
        document.removeEventListener("click", this.closeProfileSubmenu);
      }
    } catch (error) {
      console.error('Error al alternar el menú:', error);
    }
  }

  togglePlanOverlay() {
    this.showPlanOverlay = !this.showPlanOverlay;
  }

  toggleCompleteDataOverlay() {
    this.showCompleteDataOverlay = !this.showCompleteDataOverlay;
    
    // Si se está cerrando el modal, registrar en sessionStorage
    if (!this.showCompleteDataOverlay) {
      sessionStorage.setItem('completeProfileModalLastShown', Date.now().toString());
      this.hasShownModalThisSession = true;
      console.log('Modal cerrado, registrado en sessionStorage');
    }
  }

  updateOverlays(showPlan: boolean, showCompleteData: boolean) {
    this.showPlanOverlay = showPlan;
    this.showCompleteDataOverlay = showCompleteData;
  }

  redirectProfile() {
    this.router.navigate(["/dashboard/edit-profile"]);
  }

  handleLogout() {
    // Limpiar sessionStorage al cerrar sesión
    sessionStorage.removeItem('completeProfileModalLastShown');
    this.hasShownModalThisSession = false;
    
    this.authService.signOutApply();
    this.router.navigate(["/auth"]);
  }

  // Método para manejar el clic del botón flotante
  onFloatingButtonClick(): void {
    console.log('Botón flotante clickeado - Abriendo modal de video');
    this.showVideoModal = true;
  }

  // Método para obtener el tooltip del botón flotante
  getFloatingButtonTooltip(): string {
    return '¡Tienes acceso completo! Haz clic para ver el video de bienvenida';
  }

  // Método para cerrar el modal de video
  closeVideoModal(): void {
    this.showVideoModal = false;
  }

  // Método para verificar si el usuario es administrativo
  isAdminUser(): boolean {
    if (!this.user) return false;

    // Verificar si es administrativo por el type.name
    if (this.user.type && this.user.type.id === USER_TYPES.Administrator.id) {
      return true;
    }

    // O verificar si tiene el rol en el array de roles
    if (
      this.user.roles &&
      Array.isArray(this.user.roles) &&
      this.user.roles.length > 0
    ) {
      return this.user.roles.some(
        (role) => typeof role === "object" && role.name === "Administrativo"
      );
    }

    return false;
  }

  // Método para verificar si el usuario es Coach
  isCoachUser(): boolean {
    if (!this.user) return false;

    // Verificar por el tipo de usuario
    if (this.user.type && this.user.type.id === USER_TYPES.Coach.id) {
      return true;
    }

    // O verificar si tiene el rol en el array de roles
    if (
      this.user.roles &&
      Array.isArray(this.user.roles) &&
      this.user.roles.length > 0
    ) {
      return this.user.roles.some(
        (role) => typeof role === "object" && role && role.name === "Coach"
      );
    }

    return false;
  }

  isExpertUser(): boolean {
    if (!this.user) return false;

    // Verificar por el tipo de usuario
    if (this.user.type && this.user.type.id === USER_TYPES.Expert.id) {
      return true;
    }

    // O verificar si tiene el rol en el array de roles
    if (
      this.user.roles &&
      Array.isArray(this.user.roles) &&
      this.user.roles.length > 0
    ) {
      return this.user.roles.some(
        (role) => typeof role === "object" && role && role.name === "Experto"
      );
    }

    return false;
  }

  // Método para verificar si el usuario es Executive
  isExecutiveUser(): boolean {
    if (!this.user) return false;

    return !!(
      this.user.type &&
      this.user.type.id === 1 && // Customer
      this.user.type.suscriptionType &&
      this.user.type.suscriptionType.id === SUBSCRIPTION_TYPES.Executive.id.toString()
    );
  }

  handleNavigation(event: { route: string; option: any; subOption?: any }) {
    // Si no es administrador, no permitir acceso a configuración
    const isAdmin = this.isAdminUser();
    if (!isAdmin && (event.route === "config" || event.route.startsWith("admin/"))) {
      console.log("Función solo disponible para administrativos");
      return;
    }

    // Manejar rutas especiales que no están dentro del dashboard
    if (event.route === "results") {
      this.router.navigate(["/results"]);
    } else {
      // Cualquier otra ruta está permitida dentro del dashboard
      this.router.navigate(["/dashboard/" + event.route]);
    }
    
    // Si es vista móvil, colapsar el sidebar después de navegar
    if (this.isResponsiveMenu) {
      this.isCollapsed = true;
    }
  }

  isSidebarOpen = false;

  toggleSidebar() {
    this.isSidebarOpen = !this.isSidebarOpen;
  }

  getProfilePhotoUrl(): string {
    if (this.user?.personalSection?.photoBase64) {
      return this.user.personalSection.photoBase64;
    }
    return this.user?.photo || "assets/img/user-photo-default.png";
  }

  onPhotoError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = "assets/img/user-photo-default.png";
  }

  // Variables para el submenú del perfil
  showProfileSubmenu = false;

  // Método para mostrar/ocultar el submenú del perfil
  toggleProfileSubmenu(event: Event) {
    try {
      event.stopPropagation();
      this.showProfileSubmenu = !this.showProfileSubmenu;

      // Cerrar el submenú cuando se hace clic fuera de él
      if (this.showProfileSubmenu) {
        setTimeout(() => {
          try {
            document.addEventListener("click", this.closeProfileSubmenu);
          } catch (error) {
            console.error('Error al agregar event listener:', error);
          }
        }, 0);
      } else {
        // Si se está cerrando, remover el event listener
        try {
          document.removeEventListener("click", this.closeProfileSubmenu);
        } catch (error) {
          console.error('Error al remover event listener:', error);
        }
      }
    } catch (error) {
      console.error('Error al alternar el submenú de perfil:', error);
    }
  }

  closeProfileSubmenu = () => {
    try {
      this.showProfileSubmenu = false;
      document.removeEventListener("click", this.closeProfileSubmenu);
    } catch (error) {
      console.error('Error al cerrar el submenú de perfil:', error);
    }
  };

  // Método para navegar a las diferentes secciones del perfil
  navigateToProfileSection(section: string, event: Event) {
    event.stopPropagation();
    this.showProfileSubmenu = false;

    switch (section) {
      case "personalData":
        this.router.navigate(["/dashboard/edit-profile"]);
        break;
      case "professionalData":
        this.router.navigate(["/dashboard/edit-profile"], {
          fragment: "professional",
        });
        break;
      case "mentorProfile":
        this.router.navigate(["/dashboard/profile-mentor"]);
        break;
      case "plan":
        this.router.navigate(["/dashboard/plans"]);
        break;
      case "notifications":
        this.router.navigate(["/dashboard/notifications"]);
        break;
      case "accountManagement":
        this.router.navigate(["/dashboard/account-settings"]);
        break;
      case "switchAccount":
        // Aquí podría ir lógica para cambiar de cuenta
        console.log("Cambiar de cuenta");
        break;
      default:
        this.router.navigate(["/dashboard/edit-profile"]);
    }
  }

  // Métodos para verificar el tipo de usuario
  isCustomer(): boolean {
    return !!(
      this.user &&
      this.user.type &&
      this.user.type.id === 1
    );
  }

  isCoach(): boolean {
    return !!(
      this.user &&
      this.user.type &&
      this.user.type.id === 2
    );
  }

  isAdminUserType3(): boolean {
    return !!(
      this.user &&
      this.user.type &&
      this.user.type.id === 3
    );
  }

  isExpert(): boolean {
    return !!(
      this.user &&
      this.user.type &&
      this.user.type.id === 4
    );
  }

  // Método para obtener el texto del plan del usuario
  getUserPlanText(): string {
    // Si es administrador, experto o mentor, no mostrar plan
    if ( this.isAdminUser() ) {
      return 'Administrativo';
    }

    if ( this.isExpertUser() ) {
      return 'Experto';
    }

    if ( this.isCoachUser() ) {
      return 'Mentor';
    }
    
    if (this.userPlan && this.userPlan !== 'Freemiun' && this.userPlan !== 'Freemium') {
      return `Plan ${this.userPlan}`;
    }
    return 'Plan Freemium';
  }

  // Método para validar si el perfil profesional está completo
  isProfessionalProfileComplete(): boolean {
    if (!this.user) {
      return false;
    }
    
    const professionalSection = this.user.professionalSection;
    
    // Validar que exista la sección profesional
    if (!professionalSection) {
      return false;
    }

    // Validar que situacionLaboral no esté vacío
    if (!professionalSection.situacionLaboral || professionalSection.situacionLaboral.trim() === '') {
      return false;
    }

    // Validar que salaryRange no esté vacío
    if (!professionalSection.professionalData?.salaryRange || 
        professionalSection.professionalData.salaryRange.trim() === '') {
      return false;
    }

    // Validaciones específicas según situación laboral
    if (professionalSection.situacionLaboral === 'Retirado') {
      // Para retirados: validar ultimoCargo y ultimaEmpresa
      if (!professionalSection.ultimoCargo || professionalSection.ultimoCargo.trim() === '') {
        return false;
      }
      if (!professionalSection.ultimaEmpresa || professionalSection.ultimaEmpresa.trim() === '') {
        return false;
      }
    } else {
      // Para no retirados: validar situacionLaboral y job
      if (!professionalSection.situacionLaboral || professionalSection.situacionLaboral.trim() === '') {
        return false;
      }
      if (!professionalSection.professionalData?.job || 
          professionalSection.professionalData.job.trim() === '') {
        return false;
      }
    }

    return true;
  }

}